// =============================================================================
// API Utilities - 统一的API调用工具
// =============================================================================

/**
 * 统一的token键名
 */
export const TOKEN_KEY = 'auth_token';

/**
 * 获取认证头
 */
export function getAuthHeaders() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) {
    console.warn('No auth token found, user may need to login again');
    return {};
  }
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
}

/**
 * 检查是否已登录
 */
export function isAuthenticated() {
  return !!localStorage.getItem(TOKEN_KEY);
}

/**
 * 获取token
 */
export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * 设置token
 */
export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * 清除token
 */
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * 统一的API调用函数
 */
export async function apiCall(url, options = {}) {
  const headers = getAuthHeaders();
  
  // 如果没有token且不是公共API，抛出错误
  if (!headers.Authorization && !isPublicAPI(url)) {
    throw new Error('No authentication token');
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...options.headers
    }
  });

  // 处理认证错误
  if (response.status === 401) {
    console.warn('Authentication failed, redirecting to login');
    clearToken();
    window.location.href = '/login';
    throw new Error('Authentication failed');
  }

  // 处理其他HTTP错误
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response;
}

/**
 * 检查是否为公共API（不需要认证）
 */
function isPublicAPI(url) {
  const publicAPIs = [
    '/api/auth/login',
    '/api/auth/register',
    '/api/system/health',
    '/api/terminals/types/info',
    '/api/scripts/templates'
  ];
  
  return publicAPIs.some(api => url.includes(api));
}

/**
 * GET请求
 */
export async function apiGet(url, options = {}) {
  const response = await apiCall(url, {
    method: 'GET',
    ...options
  });
  return response.json();
}

/**
 * POST请求
 */
export async function apiPost(url, data, options = {}) {
  const response = await apiCall(url, {
    method: 'POST',
    body: JSON.stringify(data),
    ...options
  });
  return response.json();
}

/**
 * PUT请求
 */
export async function apiPut(url, data, options = {}) {
  const response = await apiCall(url, {
    method: 'PUT',
    body: JSON.stringify(data),
    ...options
  });
  return response.json();
}

/**
 * DELETE请求
 */
export async function apiDelete(url, options = {}) {
  const response = await apiCall(url, {
    method: 'DELETE',
    ...options
  });
  return response.json();
}

/**
 * 文件上传
 */
export async function apiUpload(url, formData, options = {}) {
  const token = getToken();
  if (!token) {
    throw new Error('No authentication token');
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      // 不设置Content-Type，让浏览器自动设置multipart/form-data边界
    },
    body: formData,
    ...options
  });

  if (response.status === 401) {
    console.warn('Authentication failed, redirecting to login');
    clearToken();
    window.location.href = '/login';
    throw new Error('Authentication failed');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * 流式请求（Server-Sent Events）
 */
export function apiStream(url, options = {}) {
  const token = getToken();
  if (!token) {
    throw new Error('No authentication token');
  }

  const eventSource = new EventSource(`${url}?token=${encodeURIComponent(token)}`);
  
  // 处理认证错误
  eventSource.addEventListener('error', (event) => {
    if (event.target.readyState === EventSource.CLOSED) {
      console.warn('Stream connection closed, possibly due to authentication failure');
    }
  });

  return eventSource;
}

/**
 * WebSocket连接
 */
export function apiWebSocket(url, protocols = []) {
  const token = getToken();
  if (!token) {
    throw new Error('No authentication token');
  }

  // 在URL中添加token参数
  const wsUrl = new URL(url, window.location.origin);
  wsUrl.searchParams.set('token', token);
  
  // 将http/https协议转换为ws/wss
  wsUrl.protocol = wsUrl.protocol.replace('http', 'ws');

  return new WebSocket(wsUrl.toString(), protocols);
}

/**
 * 批量API调用
 */
export async function apiBatch(requests) {
  const promises = requests.map(({ url, options }) => 
    apiCall(url, options).then(response => response.json())
  );
  
  return Promise.allSettled(promises);
}

/**
 * 重试机制的API调用
 */
export async function apiCallWithRetry(url, options = {}, maxRetries = 3, delay = 1000) {
  let lastError;
  
  for (let i = 0; i <= maxRetries; i++) {
    try {
      return await apiCall(url, options);
    } catch (error) {
      lastError = error;
      
      // 如果是认证错误，不重试
      if (error.message.includes('Authentication failed')) {
        throw error;
      }
      
      // 如果不是最后一次尝试，等待后重试
      if (i < maxRetries) {
        console.warn(`API call failed (attempt ${i + 1}/${maxRetries + 1}), retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= 2; // 指数退避
      }
    }
  }
  
  throw lastError;
}

/**
 * 调试信息
 */
export function getDebugInfo() {
  return {
    hasToken: isAuthenticated(),
    tokenKey: TOKEN_KEY,
    currentUrl: window.location.href,
    apiBase: window.location.origin
  };
}
